import type { CommissionRate } from "./types/index";

// Commission rates based on SKU prefixes
// NOTE: Sorted by prefix length (longest first) to ensure correct matching
export const COMMISSION_RATES: CommissionRate[] = [
  // 5-character prefixes
  { prefix: "MDPVC", rate: 0.02 }, // 2%

  // 4-character prefixes
  { prefix: "MDPP", rate: 0.02 }, // ฝากระดาษ 2%

  // 3-character prefixes
  { prefix: "MLC", rate: 0.02 }, // MLC category 2% (add specific rate if needed)
  { prefix: "MFB", rate: 0.01 }, // 1%
  { prefix: "MFA", rate: 0.01 }, // Aluminium 1%
  { prefix: "ALU", rate: 0.01 }, // Aluminium 1%
  { prefix: "MFP", rate: 0.01 }, // 1%
  { prefix: "KPB", rate: 0.02 }, // ถุงคราฟขาว - 2%
  // { prefix: "MLF", rate: 0.009 }, // Foil 0.9%

  // 2-character prefixes
  { prefix: "ML", rate: 0.02 }, // Sticker 2%
  { prefix: "MC", rate: 0.02 }, // Cup 2%
  { prefix: "MD", rate: 0.02 }, // 2%
  { prefix: "MS", rate: 0.02 }, // ช้อน 2%
  { prefix: "MK", rate: 0.0 }, // 0%
  { prefix: "MI", rate: 0.02 }, // 2%
  { prefix: "MB", rate: 0.02 }, // Box 2%
  { prefix: "MP", rate: 0.02 }, // ป้าย - 2%
  { prefix: "BP", rate: 0.02 }, // กระดาษรองอบ - 2%
  { prefix: "MR", rate: 0.02 }, // Ribbon - 2%
  { prefix: "MF", rate: 0.01 }, // Foil 1%
];

// OpenSearch configuration
export const OPENSEARCH_CONFIG = {
  WORKFLOW_ID: "9bWRSh",
  DOCUMENT_TYPE: "TASK",
  INDEX_NAME: "masterlabel-tasks",
  BATCH_SIZE: 10000,
};

// Commission period configuration
export const COMMISSION_CONFIG = {
  CUTOFF_DAY: 26, // 26th of each month
  TIMEZONE: "Asia/Bangkok",
  PAYMENT_WINDOW_MONTHS: 7, // Only count payments within N months of invoice creation
};

// Payment status constants
export const PAYMENT_STATUS = {
  PAID: "PAID",
  PARTIAL_PAYMENT: "PARTIAL_PAYMENT",
} as const;

// Task status constants
export const TASK_STATUS = {
  PAID: "PAID",
  PARTIAL_PAYMENT: "PARTIAL_PAYMENT",
} as const;

// Thailand VAT configuration
export const VAT_CONFIG = {
  VAT_RATE: 0.07, // 7% VAT rate in Thailand
  VAT_MULTIPLIER: 1.07, // Multiplier to add VAT (1 + 0.07)
} as const;

// Production workflow constants
export const PRODUCTION_WORKFLOW_ID = '1BFmjC';
export const QC_PROCESS_TITLE = 'ตรวจสอบ';
export const PROD_STAGES: Record<string, string> = {
  CunKjm: 'ใบสั่งงานใหม่',
  '5o05kb': 'วางแผนการผลิต',
  '1n2plz': 'จ่ายวัตถุดิบตามเครื่อง',
  '6y7iyx': 'รอผลิต',
  '2eCtIk': 'ตรวจสอบสินค้า',
  '5TXaVD': 'แพ็คสินค้า',
  SUCCESS: 'เสร็จสิ้น',
  VOIDED: 'ยกเลิก',
};

// Canonical production process whitelist + ordering.
// Sourced from distinct ref2 values observed in OpenSearch for workflowId=1BFmjC,
// plus ตรวจสอบ from QCAndPackProcess (not surfaced in ref2 but is the final inspection stage).
// Order requested by ops team — physical workflow + inspection priority; remainder trails.
export const PROD_PROCESS_ORDER: readonly string[] = [
  // Requested order
  'พิมพ์สี',           // 1. print
  'พิมพ์สี+เซลล์​ดูสี', // 2. print + color-check (contains ZWSP between เซลล์ and ดูสี)
  'พิมพ์ซิลค์​สกรีน',  // 3. silk-screen print (contains ZWSP between ซิลค์ and สกรีน)
  'พิมพ์สี-ปั้มมีด',   // 4. combined print + die cut
  'ปั้มมีด',           // 5. die cut
  'QC',               // 6. QC-Auto (productionProcess; machine = "Auto")
  'สลิดขอบข้าง',       // 7. edge slit
  'ตรวจสอบเบื้อนต้น',  // 8. initial inspection (note: source data spells เบื้อน)
  'ตรวจสอบ',           // 9. final manual inspection (QCAndPackProcess)
  'ขึ้นรูป',           // 10. forming

  // Remaining ref2 values — ordered by observed frequency
  'รันนัมเบอร์',
  'เคลือบ OPP',
  'ล้างกาว OPP',
  'ปะกาว',
  'เคลือบกาว 2 หน้า',
  'ชอร์ตกระดาษ',
  'QC Auto',
  'ปั้มฟอยล์',
];

export const PROD_PROCESS_WHITELIST: ReadonlySet<string> = new Set(PROD_PROCESS_ORDER);

// Process index map (used to sort entries by canonical order). Unknown processes get Infinity.
export const PROD_PROCESS_INDEX: Readonly<Record<string, number>> = Object.freeze(
  PROD_PROCESS_ORDER.reduce<Record<string, number>>((acc, title, i) => {
    acc[title] = i;
    return acc;
  }, {}),
);

export function getProcessOrderIndex(processTitle: string): number {
  const idx = PROD_PROCESS_INDEX[processTitle];
  return idx === undefined ? Number.POSITIVE_INFINITY : idx;
}
